import {
  Badge,
  Button,
  Col,
  Divider,
  Drawer,
  Dropdown,
  Flex,
  Form,
  Grid,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Tooltip,
  Upload,
  message,
  theme,
  notification,
  Modal
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EditOutlined,
  GoogleOutlined,
  DeleteOutlined,
  VideoCameraOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  RightOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import UserProfileAPI from "../../../restAPI/UserProfileAPI";
import validateForm from "../../../utils/FormValidation";
import validationRules from "../../../assets/validation/rules"; //../../assets/validation/rules
import { setProfileDataUpdate } from "../../../redux/slices/profileSlice";
import CommonSkeletonLoader from "../../../helperComponents/CommonSkeleton";
import Empty_profile_pic from "../../../assets/image/empty_profile_pic.png";
import useCurrentCompany from "../../../hooks/useCurrentCompany";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const { TextArea } = Input;
const { Dragger } = Upload;

export default function UserProfile({ setUserProfileChanged = false }) {
  const [form] = Form.useForm();
  const prevCompanyIdRef = useRef(null);
  const dispatch = useDispatch();
  const { companyId, token, userCompany } = useSelector((state) => state.auth);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // State for edit mode  
  const currentCompanyInfo = useCurrentCompany();
  const [userProfile, setUserProfile] = useState({});
  const [file, setFile] = useState();
  const [isModified, setIsModified] = useState(false);
  const [isUploadImage, setIsUploadImage] = useState(false);
  const [isContentChange, setIsContentChange] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [payload, setPayload] = useState({
    companyName: "",
    name: "",
    designation: "",
    mobileNumber: "",
    email: "",
    department: "",
    branch: "",
    address: "",
  });
  const [fileList, setFileList] = useState([
    {
      uid: "-1",
      name: "image.png",
      status: "done",
      url: Empty_profile_pic,
    },
  ]);
  const initialized = useRef(false);
  const [profileDataLoading, setProfileDataLoading] = useState(false);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement,
    });
  };

  const props = {
    name: "file",
    multiple: false,
    maxCount: 1,
    beforeUpload: (file) => {
      setFile(file);
      setIsModified(true);
      setIsUploadImage(true);
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
        },
      ]);
      return false;
    },
  };

  const handleRemove = async (file) => {
    if (isUploadImage) {
      setIsModified(false);
      setIsUploadImage(false);
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: Empty_profile_pic,
        },
      ]);
      return;
    }
    try {
      const response = await UserProfileAPI.deleteProfilePicture(
        currentCompanyInfo.userDetailsId,
        companyId,
        token
      );
      if (response.status === 200) {
        setIsModified(false);
        setIsUploadImage(false);
        setFileList([
          {
            uid: "-1",
            name: "image.png",
            status: "done",
            url: Empty_profile_pic,
          },
        ]);
        openNotificationWithIcon(
          "success",
          "Successful",
          `${response.data.message}`,
          "bottomLeft"
        );
        setUserProfileChanged(true);
        dispatch(setProfileDataUpdate(true));
      }
    } catch (error) {
      dispatch(setProfileDataUpdate(false));
      setUserProfileChanged(false);
      console.log("error in delete profile picture :", error);
      if (error.response.data) {
        openNotificationWithIcon(
          "error",
          "Process failed",
          `${error.response.data.message}`,
          "bottomLeft"
        );
      } else {
        openNotificationWithIcon(
          "error",
          "Process failed",
          `Profile picture delete failed.`,
          "bottomLeft"
        );
      }
    }
  };

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  useEffect(() => {
    if (!initialized.current) {
      setProfileDataLoading(true)
      initialized.current = true;          
      getProfileData(currentCompanyInfo);
    }
  }, []);

  useEffect(() => {
    if (prevCompanyIdRef.current === null) {
      prevCompanyIdRef.current = companyId;
      return;
    }

    if (prevCompanyIdRef.current !== companyId) {
      
      getProfileData(currentCompanyInfo);

      prevCompanyIdRef.current = companyId;
    }
  }, [companyId]);


  const onPreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const imgWindow = window.open(src);
    imgWindow?.document.write(`<img src="${src}" />`);
  };

  const handlePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };
  const handleCancel = () => setPreviewVisible(false);

  const getBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  
  const getProfileData = async (companyInfo) => {
    if (!companyInfo) return;
    await UserProfileAPI.getUserProfileByUserDetailsId(
      companyInfo.userDetailsId,
      companyId,
      token
    )
      .then((res) => {
        if (res?.data) {
          setUserProfile(res.data);
          form.setFieldsValue({
            companyName: res?.data?.company?.name || 'N / A',
            userName: res?.data?.name || 'N / A',
            designation: res?.data?.designation || 'N / A',
            mobileNumber: res?.data?.personalNumber || 'N / A',
            userEmail: res?.data?.email || 'N / A',
            userDept: res?.data?.department?.name || 'N / A',
            userBranch: res?.data?.branch?.name || 'N / A',
            userAddress: res?.data?.address || 'N / A',
        });
          setFileList([{ 
            uid: "-1",
            name: "image.png",
            status: "done",
            url: res.data.profilePicture || Empty_profile_pic 
          }]);
        }
      })
      .catch((err) => {
        console.log("error getting esignature data :", err.message);
      }).finally(() => {
        setProfileDataLoading(false)
      })
  };

  const handleCancelChange = (e) => {
    setEditMode(false);
    if (isModified) {
      setProfileDataLoading(true);
      getProfileData(currentCompanyInfo);
      setIsModified(false);
      setIsContentChange(false);
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode); // Toggle edit mode
    if (isModified) {
      getProfileData(currentCompanyInfo);
      setIsModified(false);
      setIsContentChange(false);
    }
  };

  const handleSaveChange = async () => {
    if (isModified) {
      setSaveButtonLoading(true);
      try {
        //is upload image true
        if (isUploadImage) {
          const formData = new FormData();
          formData.append("profilePicture", file);
          await UserProfileAPI.uploadProfilePicture(
            formData,
            currentCompanyInfo.userDetailsId,
            companyId,
            token
          )
            .then((response) => {
              openNotificationWithIcon("success","Successful",`${response.message}`,"bottomLeft");
              setUserProfileChanged(true);
              setSaveButtonLoading(false);
              dispatch(setProfileDataUpdate(true));
            })
            .catch((err) => {
              setUserProfileChanged(false);
              setSaveButtonLoading(false);
              dispatch(setProfileDataUpdate(false));
              if (err.response) {
                openNotificationWithIcon("error","Profile picture upload failed",`${err.response.data.message}`,"bottomLeft");
              } else {
                openNotificationWithIcon("error","Profile picture upload failed",`${err.message}`,"bottomLeft");
              }
            });
        }

        //is content change true
        if (isContentChange) {
          const isValidate = await form.validateFields();
          const values = await form.getFieldsValue()

          const uploadContent = {
            name: values.userName,
            personalNumber: values.mobileNumber,
            address: values.userAddress,
          };
          //console.log(uploadContent);
          await UserProfileAPI.updateUserProfile(
            uploadContent,
            currentCompanyInfo.userDetailsId,
            companyId,
            token
          )
            .then((response) => {
              openNotificationWithIcon("success","Successful",`${response.message}`,"bottomLeft");
              setUserProfileChanged(true);
              dispatch(setProfileDataUpdate(true));
            })
            .catch((err) => {
              console.log('error in save')
              setUserProfileChanged(false);
              dispatch(setProfileDataUpdate(false));
              if (err.response) {
                openNotificationWithIcon("error","Profile update failed",`${err.response.data.message}`,"bottomLeft");
              } else {
                openNotificationWithIcon("error","Profile update failed",`${err.message}`,"bottomLeft");
              }
            }).finally(() => {
              setSaveButtonLoading(false);
            })
          }
          setProfileDataLoading(true)
          setIsModified(false);
          setIsContentChange(false);
          setEditMode(false);
          setIsUploadImage(false);
          getProfileData(currentCompanyInfo);
      } catch (error) {
          setSaveButtonLoading(false);
          console.log('error in save form :', error)
        }
        
      } else {
      setSaveButtonLoading(false);
      openNotificationWithIcon("error","User profile creation failed","error","bottomLeft");
    }
  };

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules);
    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
    } else {
      const keyData = Object.keys(data)[0];
      setErrors((prev) => ({ ...prev, [keyData]: "" }));
    }
  }

  return (
    <>
      <div
        style={{
          margin: "16px",
          padding: "8px 12px",
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Flex justify="space-between" align="center">
          <Flex justify="flex-start" align="center" gap={16}>
            <span>User Profile</span>
          </Flex>
          {!editMode && (
            <Button type="primary" onClick={handleEditToggle}>
              Edit
            </Button>
          )}
        </Flex>
      </div>
      {
        profileDataLoading ? <CommonSkeletonLoader itemCount={3} /> : (
          <div
            style={{
              margin: "16px",
              padding: "24px 12px",
              minHeight: "70svh",
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Flex align="center" justify="center" vertical={true}>
              <ImgCrop rotationSlider>
                <Upload
                  listType="picture-circle"
                  fileList={fileList}
                  onChange={({ fileList: newFileList }) => {
                    // If fileList becomes empty (after remove), set default image
                    if (newFileList.length === 0) {
                      setFileList([{
                        uid: "-1",
                        name: "image.png",
                        status: "done",
                        url: Empty_profile_pic,
                      }]);
                    } else {
                      setFileList(newFileList);
                    }
                  }}
                  onPreview={handlePreview}
                  onRemove={handleRemove} 
                  showUploadList={{
                    showPreviewIcon: fileList[0]?.url && fileList[0]?.url !== Empty_profile_pic, // hide preview icon if no profile picture or default image
                    showRemoveIcon: editMode && !!fileList[0]?.url && fileList[0]?.url !== Empty_profile_pic, // hide remove icon if no profile picture, default image, or not in edit mode
                    showDownloadIcon: false,
                  }}
                />
              </ImgCrop>
              <Modal
                open={previewVisible}
                // visible={previewVisible}
                footer={null}
                onCancel={handleCancel}
              >
                <img alt="preview" style={{ width: '100%' }} src={previewImage} />
              </Modal>
              {editMode && (
                <div style={{ marginTop: -18, textAlign: "center" }}>
                  <Upload {...props} showUploadList={false}>
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<UploadOutlined />}
                    />
                  </Upload>
                </div>
              )}
            </Flex>
            <br />
            <Form layout="vertical" variant="filled" form={form} onValuesChange={() => setIsModified(true)}>
              <Row gutter={16}>
                <Col lg={12} md={24} sm={24}>
                  <Form.Item label="Company Name" name="companyName">
                    <Input
                      disabled
                      name="companyName"
                      // value={payload["companyName"]}
                      placeholder="Company Name"
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} md={24} sm={24}>
                  {/* <Form.Item label="Name" validateStatus={errors.name ? "error" : ""} help={errors.name || ""}> */}
                  <Form.Item 
                    label="Name" 
                    name="userName"
                    rules={[
                      {max: 50, message: 'Name must be less than 50 characters.'}
                    ]}
                  >
                    <Input
                      name="name"
                      onChange={(e) => {
                      //   const value = e.target.value;
                      //   setPayload((prev) => ({ ...prev, name: e.target.value }));
                      //   setIsModified(true);
                        setIsContentChange(true);
                      //   // Manual validation
                      //   if(value.length > 50){
                      //     setErrors((prev) => ({...prev, name: "Name must be less than 50 characters."}))
                      //   } else{
                      //     setErrors((prev) => ({...prev, name: ""}))
                      //   }
                      }}
                      // value={payload["name"]}
                      placeholder="Name"
                      disabled={!editMode}
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} md={24} sm={24}>
                  <Form.Item label="Designation" name="designation">
                    <Input
                      disabled
                      name="designation"
                      // value={payload["designation"]}
                      placeholder="Designation"
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} md={24} sm={24}>
                  <Form.Item
                    label="Mobile Number"
                    // validateStatus={errors.mobileNumber ? "error" : ""}
                    // help={errors.mobileNumber || ""}
                    name='mobileNumber'
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || value === 'N / A') {
                            return Promise.resolve();
                          }
                          if (value.length < 9 || value.length > 15) {
                            return Promise.reject(new Error('Mobile number must be between 9 and 15 digits.'));
                          }
                          if (!/^\d+$/.test(value)) {
                            return Promise.reject(new Error('Mobile number must contain only digits'));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Input
                      disabled={!editMode}
                      name="mobileNumber"
                      onChange={(e) => {
                      //   setPayload((prev) => ({
                      //     ...prev,
                      //     mobileNumber: e.target.value,
                      //   }));
                      //   setIsModified(true);
                        setIsContentChange(true);
                      //   const validateData = {
                      //     mobileNumber: e.target.value,
                      //   };
                      //   validateFormData(validateData);
                      }}
                      // value={payload["mobileNumber"]}
                      placeholder="Mobile Number"
                    />
                  </Form.Item>
                </Col>
                <Col lg={24} md={24} sm={24}>
                  <Form.Item label="Email" name='userEmail'>
                    <Input
                      disabled
                      name="email"
                      // value={payload["email"]}
                      placeholder="Email"
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} md={24} sm={24}>
                  <Form.Item label="Department" name='userDept'>
                    <Input
                      disabled
                      name="department"
                      // value={payload["department"]}
                      placeholder="Department"
                    />
                  </Form.Item>
                </Col>
                <Col lg={12} md={24} sm={24}>
                  <Form.Item label="Branch" name='userBranch'>
                    <Input
                      disabled
                      name="branch"
                      // value={payload["branch"]}
                      placeholder="Branch"
                    />
                  </Form.Item>
                </Col>
                <Col lg={24} md={24} sm={24}>
                  <Form.Item label="Address" name='userAddress' rules={[
                    {max: 255, message: 'Address must be less than 255 characters.'}
                  ]}>
                    <TextArea
                      disabled={!editMode}
                      name="address"
                      onChange={(e) => {
                      //   setPayload((prev) => ({
                      //     ...prev,
                      //     address: e.target.value,
                      //   }));
                      //   setIsModified(true);
                        setIsContentChange(true);
                      }}
                      // value={payload["address"]}
                      placeholder="Address"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            {editMode && (
              <Flex align="center" justify="flex-end" gap={16}>
                <Button type="default" onClick={handleCancelChange}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveChange}
                  disabled={!isModified}
                  loading={saveButtonLoading}
                >
                  Save
                </Button>
              </Flex>
            )}
          </div>
        )
      }
      {contextHolder}
    </>
  );
}
